import { type FormEvent, useState } from 'react';
import { ERROR_CODES } from '@betreuung/shared';
import { LayoutGrid } from 'lucide-react';
import { useLogin } from '@/api/mutations';
import { ApiError } from '@/api/http';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { de } from '@/i18n/de';

/**
 * Sign-in card.
 *
 * The name is a free-text field rather than a picker: the specification calls
 * that out for privacy, so an unauthenticated visitor learns nothing about who
 * uses the app. The error message is intentionally the same for a wrong password
 * and an unknown name.
 */
export function LoginScreen() {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const login = useLogin();

  const errorMessage = (() => {
    if (!login.isError) return null;
    if (login.error instanceof ApiError && login.error.code === ERROR_CODES.RATE_LIMITED) {
      return de.login.rateLimited;
    }
    if (login.error instanceof ApiError && login.error.status === 401) return de.login.failed;
    return de.errors.generic;
  })();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (login.isPending) return;
    login.mutate({ name: name.trim(), password });
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface px-4">
      <div className="w-full max-w-[360px] rounded-panel border border-line bg-card px-6 pt-10 pb-8 shadow-card">
        <div className="pb-1.5 text-center">
          <span className="inline-flex size-11 items-center justify-center rounded-card bg-accent text-accent-fg">
            <LayoutGrid className="size-[22px]" />
          </span>
        </div>
        <h1 className="text-center text-screen font-bold text-fg">{de.login.title}</h1>
        <p className="pt-1 pb-5 text-center text-label text-fg-muted">{de.login.subtitle}</p>

        <form onSubmit={onSubmit} noValidate>
          <label htmlFor="login-name" className="section-label block pb-2">
            {de.login.name}
          </label>
          <Input
            id="login-name"
            name="name"
            autoComplete="username"
            autoCapitalize="words"
            required
            value={name}
            invalid={Boolean(errorMessage)}
            onChange={(event) => setName(event.target.value)}
            className="px-3 py-[11px]"
          />

          <label htmlFor="login-password" className="section-label block pt-4 pb-2">
            {de.login.password}
          </label>
          <Input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            invalid={Boolean(errorMessage)}
            onChange={(event) => setPassword(event.target.value)}
            className="px-3 py-[11px]"
          />

          {errorMessage ? (
            <p role="alert" className="pt-2 text-label text-cal-9">
              {errorMessage}
            </p>
          ) : null}

          <Button
            type="submit"
            className="mt-4"
            disabled={login.isPending || name.trim() === '' || password === ''}
          >
            {login.isPending ? de.login.submitting : de.login.submit}
          </Button>
        </form>

        {/* Sessions are always remembered on the device, so this is a statement
            of fact rather than an option — matching the design reference. */}
        <p className="pt-3 text-center text-duration leading-relaxed text-fg-hint">
          {de.login.staySignedIn}
        </p>
      </div>
    </div>
  );
}
